import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  GearModel,
  Manufacturer,
  PaginatedResponse,
  ParseSerialRequest,
  ParseSerialResponse,
  AssetType,
  CreateCustomGearModelPayload,
} from '@/lib/types';

export interface GearModelFilters {
  page?: number;
  page_size?: number;
  q?: string;
  manufacturer?: number;
  manufacturer_name?: string;
  asset_category?: AssetType | '';
  category?: AssetType | '';
  is_verified?: boolean;
  ordering?: string;
}

export function useGearModel(id: number) {
  return useQuery({
    queryKey: queryKeys.gearModels.detail(id),
    queryFn: async (): Promise<GearModel> => {
      const response = await apiClient.get<GearModel>(`/api/v1/gear-models/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useManufacturer(id: number) {
  return useQuery({
    queryKey: queryKeys.manufacturers.detail(id),
    queryFn: async (): Promise<Manufacturer> => {
      const response = await apiClient.get<Manufacturer>(`/api/v1/manufacturers/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useParseSerial() {
  return useMutation({
    mutationFn: async (data: ParseSerialRequest): Promise<ParseSerialResponse> => {
      const response = await apiClient.post<ParseSerialResponse>(
        '/api/v1/assets/parse-serial/',
        data
      );
      return response.data;
    },
  });
}

export function useGearModelSearch(searchQuery: string, category?: AssetType) {
  const filters: GearModelFilters = {
    q: searchQuery,
    page_size: 20,
  };
  if (category) {
    filters.asset_category = category;
  }

  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.gearModels.list(cleanFilters),
    queryFn: async (): Promise<GearModel[]> => {
      const response = await apiClient.get<PaginatedResponse<GearModel>>('/api/v1/gear-models/', {
        params: cleanFilters,
      });
      return response.data.results;
    },
    enabled: searchQuery.length >= 2,
  });
}

export function useCreateCustomGearModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCustomGearModelPayload): Promise<GearModel> => {
      const response = await apiClient.post<GearModel>('/api/v1/gear-models/create-custom/', data);
      return response.data;
    },
    onSuccess: (_, _variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gearModels.all });
    },
  });
}
